// components/shared/PeoplePicker.jsx — O365 people search picker
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { Avatar, Spinner, Text } from "@fluentui/react-components";
import { Dismiss16Regular, Search24Regular, Person24Regular } from "@fluentui/react-icons";
import { searchO365Users } from "../../services/graphUserService";

/**
 * PeoplePicker — searches O365 users as the user types.
 *
 * Props:
 *   value      { name, email, jobTitle?, department? } | null
 *   onChange   (user | null) => void
 *   label      string
 *   required   boolean
 *   disabled   boolean  — shows selected user but disables clearing / changing
 *   placeholder string
 */
export default function PeoplePicker({
  value,
  onChange,
  label,
  required = false,
  disabled = false,
  placeholder = "Search by name or email...",
}) {
  const { instance, accounts } = useMsal();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchMode, setSearchMode] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualName, setManualName] = useState("");
  const [manualEmail, setManualEmail] = useState("");

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function onMouseDown(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  const doSearch = useCallback(
    async (q) => {
      if (q.length < 2) { setResults([]); setOpen(false); return; }
      setSearching(true);
      const { users, searchMode: mode } = await searchO365Users(instance, accounts, q);
      setResults(users);
      setSearchMode(mode);
      if (mode === "failed" && users.length === 0) setManualMode(true);
      else setOpen(true);
      setSearching(false);
    },
    [instance, accounts],
  );

  function handleChange(e) {
    const q = e.target.value;
    setQuery(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 300);
  }

  function select(user) {
    onChange(user);
    setQuery("");
    setResults([]);
    setOpen(false);
    setSearchMode(null);
  }

  function clear() {
    onChange(null);
    setQuery("");
    setResults([]);
    setOpen(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function applyManual() {
    if (!manualEmail.trim()) return;
    onChange({
      id: "",
      name: manualName.trim() || manualEmail.trim(),
      email: manualEmail.trim(),
      jobTitle: "",
      department: "",
    });
    setManualMode(false);
    setManualName("");
    setManualEmail("");
  }

  // ── Selected user card ──────────────────────────────────────────────────────
  if (value && !manualMode) {
    return (
      <div>
        {label && (
          <div style={{ fontSize: 14, fontWeight: 600, color: "#201F1E", marginBottom: 6 }}>
            {label}{required && <span style={{ color: "#D13438" }}> *</span>}
          </div>
        )}
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "10px 14px",
          background: disabled ? "#F8F9FC" : "#F0F6FF",
          border: `1.5px solid ${disabled ? "#E8EAED" : "#C0D4FF"}`,
          borderRadius: 10,
        }}>
          <Avatar name={value.name} size={36} color="brand" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ fontWeight: 700, color: "#001A3D", fontSize: 14, display: "block" }}>{value.name}</Text>
            {(value.jobTitle || value.department) && (
              <Text style={{ fontSize: 12, color: "#555", display: "block" }}>
                {[value.jobTitle, value.department].filter(Boolean).join(" · ")}
              </Text>
            )}
            <Text style={{ fontSize: 12, color: "#0066FF" }}>{value.email}</Text>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={clear}
              title="Remove"
              style={{ border: "none", background: "none", cursor: "pointer", color: "#888", padding: 4, display: "flex", alignItems: "center", borderRadius: 4 }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "#D13438"; e.currentTarget.style.background = "#FDE7E9"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "#888"; e.currentTarget.style.background = "none"; }}
            >
              <Dismiss16Regular />
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Manual entry fallback ───────────────────────────────────────────────────
  if (manualMode) {
    return (
      <div>
        {label && (
          <div style={{ fontSize: 14, fontWeight: 600, color: "#201F1E", marginBottom: 6 }}>
            {label}{required && <span style={{ color: "#D13438" }}> *</span>}
          </div>
        )}
        <div style={{ background: "#FFFCE6", border: "1px solid #FFE066", borderRadius: 8, padding: "8px 12px", marginBottom: 10, fontSize: 12, color: "#7A5F00" }}>
          O365 directory search is not available — enter user details manually.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
          <input
            value={manualName}
            onChange={(e) => setManualName(e.target.value)}
            placeholder="Full name"
            style={{ padding: "8px 10px", border: "1px solid #EDEBE9", borderRadius: 6, fontSize: 13, outline: "none" }}
          />
          <input
            type="email"
            value={manualEmail}
            onChange={(e) => setManualEmail(e.target.value)}
            placeholder="Email address *"
            style={{ padding: "8px 10px", border: "1px solid #EDEBE9", borderRadius: 6, fontSize: 13, outline: "none" }}
          />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={applyManual}
            disabled={!manualEmail.trim()}
            style={{ padding: "7px 16px", background: "#0066FF", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, cursor: "pointer", opacity: manualEmail.trim() ? 1 : 0.5 }}
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={() => setManualMode(false)}
            style={{ padding: "7px 16px", background: "transparent", color: "#666", border: "1px solid #EDEBE9", borderRadius: 6, fontSize: 13, cursor: "pointer" }}
          >
            Search instead
          </button>
        </div>
      </div>
    );
  }

  // ── Search input + dropdown ─────────────────────────────────────────────────
  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      {label && (
        <div style={{ fontSize: 14, fontWeight: 600, color: "#201F1E", marginBottom: 6 }}>
          {label}{required && <span style={{ color: "#D13438" }}> *</span>}
        </div>
      )}
      <div style={{ position: "relative" }}>
        <Search24Regular style={{
          position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
          color: "#999", fontSize: 16, pointerEvents: "none",
        }} />
        <input
          ref={inputRef}
          value={query}
          onChange={handleChange}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            width: "100%", boxSizing: "border-box",
            padding: "9px 36px",
            border: "1px solid #EDEBE9", borderRadius: 8, fontSize: 13,
            outline: "none", background: disabled ? "#F8F9FC" : "#fff",
            transition: "border-color 0.15s",
          }}
          onFocusCapture={(e) => { e.target.style.borderColor = "#0066FF"; }}
          onBlurCapture={(e) => { e.target.style.borderColor = "#EDEBE9"; }}
        />
        {searching && (
          <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}>
            <Spinner size="tiny" />
          </div>
        )}
      </div>

      {/* Hint text */}
      {!searching && query.length > 0 && query.length < 2 && (
        <Text style={{ fontSize: 11, color: "#999", marginTop: 4, display: "block" }}>Type at least 2 characters to search</Text>
      )}

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
          background: "#fff", border: "1px solid #E0E7FF", borderRadius: 12,
          boxShadow: "0 8px 28px rgba(0,26,61,0.14)", zIndex: 1500,
          maxHeight: 300, overflowY: "auto",
        }}>
          {results.length === 0 ? (
            <div style={{ padding: "16px 18px", textAlign: "center", color: "#888", fontSize: 13 }}>
              No users found for "{query}".
              <button
                type="button"
                onClick={() => { setOpen(false); setManualMode(true); }}
                style={{ marginLeft: 8, fontSize: 12, color: "#0066FF", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 }}
              >
                Enter manually
              </button>
            </div>
          ) : (
            <>
              {results.map((user) => (
                <button
                  key={user.id || user.email}
                  type="button"
                  onClick={() => select(user)}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "10px 16px", width: "100%",
                    border: "none", borderBottom: "1px solid #F3F2F1",
                    background: "transparent", cursor: "pointer", textAlign: "left",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#F0F6FF"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <Avatar name={user.name} size={32} color="neutral" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#001A3D", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.name}</div>
                    {(user.jobTitle || user.department) && (
                      <div style={{ fontSize: 11, color: "#888" }}>{[user.jobTitle, user.department].filter(Boolean).join(" · ")}</div>
                    )}
                    <div style={{ fontSize: 11, color: "#0066FF", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</div>
                  </div>
                  <Person24Regular style={{ color: "#CCC", flexShrink: 0, fontSize: 16 }} />
                </button>
              ))}
              {searchMode === "people" && (
                <div style={{ padding: "8px 16px", background: "#F8F9FC", borderTop: "1px solid #EDEBE9", fontSize: 11, color: "#888", lineHeight: 1.5 }}>
                  Showing contacts from your O365 network. For the full directory, your tenant admin may need to grant <em>User.ReadBasic.All</em>.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
